import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { FaEllipsisH } from 'react-icons/fa';
import { IoIosGitNetwork } from 'react-icons/io';
import classNames from 'classnames';
import { Classes } from '@blueprintjs/core';
import storeLoader from 'app/stores/storeLoader';
import { Menu, MenuItem, Popover, Suspense, Text, Tag, Box } from 'core/components';
import { getHealthStateFromHealthData } from 'app/views/hybrid/utils/health';
import { createCleanNodeData as createCleanNodeDataUtil } from 'app/views/hybrid/utils/map';
import { capitalize } from 'lodash';
import { ENTITY_TYPES } from '@kentik/ui-shared/hybrid/constants';
import { makeConfig } from './configHelper';
import { getVpcEndpointProps } from './cloudUtil';

const {
  CUSTOMER_GATEWAY,
  DIRECT_CONNECTION_GATEWAY,
  INTERNET_GATEWAY,
  NAT_GATEWAY,
  ROUTER,
  TRANSIT_GATEWAY,
  TRANSIT_GATEWAY_ATTACHMENT
} = ENTITY_TYPES.get('aws');

const {
  VNET,
  SUBNET,
  LOCATION,
  VPN_SITE,
  VIRTUAL_WAN,
  VIRTUAL_HUB,
  LOCAL_NETWORK_GATEWAY,
  EXPRESS_ROUTE_CIRCUIT,
  LOAD_BALANCER,
  VNET_GATEWAY,
  FIREWALL,
  APPLICATION_GATEWAY
} = ENTITY_TYPES.get('azure');

const {
  VIRTUAL_CLOUD_NETWORK,
  SUBNET: OCI_SUBNET,
  INTERNET_GATEWAY: OCI_INTERNET_GATEWAY,
  NAT_GATEWAY: OCI_NAT_GATEWAY,
  SERVICE_GATEWAY,
  DYNAMIC_ROUTING_GATEWAY,
  LOCAL_PEERING_GATEWAY,
  INSTANCE,
  ROUTER: OCI_ROUTER
} = ENTITY_TYPES.get('oci');

const {
  NETWORK,
  SUBNET: GCP_SUBNET,
  EXTERNAL_VPN_GATEWAY,
  ROUTER: GCP_ROUTER,
  INTERCONNECT,
  INTERCONNECT_ATTACHMENT,
  VPN_GATEWAY,
  VPN_TUNNEL,
  CLOUD_ROUTER,
  TARGET_VPN_GATEWAY,
  NAT_GATEWAY: GCP_NAT_GATEWAY,
  REGION: GCP_REGION,
  FIREWALL: GCP_FIREWALL
} = ENTITY_TYPES.get('gcp');

const defaultPopoverConfig = {
  positionOffset: {
    top: 0,
    left: -20
  }
};

// Helper function to map node types to entity types for CloudIcon
const getEntityTypeForIcon = (nodeType, nodeData = {}) => {
  switch (nodeType) {
    // AWS entity types
    case TRANSIT_GATEWAY:
      return 'transitGateway';
    case TRANSIT_GATEWAY_ATTACHMENT:
      return 'transitGateway';
    case INTERNET_GATEWAY:
      return 'internetGateway';
    case CUSTOMER_GATEWAY:
      return 'customerGateway';
    case 'VirtualGateways':
      return 'virtualPrivateGateway';
    case DIRECT_CONNECTION_GATEWAY:
      return 'directConnectGateway';
    case NAT_GATEWAY:
      return 'natGateway';
    case 'gateway':
      // Determine specific gateway type based on nodeData
      if (
        nodeData?.type === TRANSIT_GATEWAY ||
        nodeData?.GatewayType === 'Transit' ||
        (typeof nodeData?.value === 'string' && nodeData.value.startsWith('tgw-'))
      ) {
        return 'transitGateway';
      }
      // Default to internet gateway for other gateways
      return 'internetGateway';
    case ROUTER:
      return 'router';
    case 'CoreNetworkAttachment':
      return 'coreNetworkAttachment';
    case 'CoreNetworkEdge':
      return 'coreNetworkEdge';
    case 'Firewall':
      return 'firewall';

    // GCP entity types
    case NETWORK:
      return 'network';
    case GCP_SUBNET:
      return 'subnets';
    case EXTERNAL_VPN_GATEWAY:
      return 'externalVpnGateway';
    case GCP_ROUTER:
      return 'router';
    case INTERCONNECT:
      return 'interconnect';
    case INTERCONNECT_ATTACHMENT:
      return 'interconnectAttachment';
    case VPN_GATEWAY:
      return 'vpnGateway';
    case VPN_TUNNEL:
      return 'vpnTunnel';
    case CLOUD_ROUTER:
      return 'cloudRouter';
    case TARGET_VPN_GATEWAY:
      return 'targetVpnGateway';
    case GCP_NAT_GATEWAY:
      return 'natGateway';
    case GCP_REGION:
      return 'region';
    case GCP_FIREWALL:
      return 'firewall';

    default:
      return nodeType; // Fallback to the original type
  }
};

export default function withPopover(WrappedComponent, passedPopoverConfig = {}) {
  const popoverConfig = { ...defaultPopoverConfig, ...passedPopoverConfig };
  @storeLoader('$devices', '$sites')
  @inject('$hybridMap', '$journeys')
  @withRouter
  @observer
  class WrapperComponent extends React.Component {
    state = {
      position: null,
      config: null,
      panel: null
    };

    boxRef = React.createRef();

    clickX = 0;

    clickY = 0;

    componentDidMount() {
      window.addEventListener('click', this.handleMouseClick, { capture: true });
    }

    componentWillUnmount() {
      window.removeEventListener('click', this.handleMouseClick);
    }

    componentDidUpdate(prevProps) {
      const { location } = this.props;

      if (prevProps.location.pathname !== location.pathname) {
        // close the popover on location change
        this.handlePopoverClose();
      }
    }

    setPopoverPosition = ({ left, top }) => {
      this.clickX = left;
      this.clickY = top;
    };

    handleMouseClick = (e) => {
      const clickRect = e.target.getBoundingClientRect();

      this.clickX = clickRect.x;
      this.clickY = clickRect.y;
    };

    handlePopoverOpen = ({ position, offset, panel, ...initialConfig }) => {
      const { setSidebarDetails, $journeys } = this.props;
      const offsetRect = this.boxRef.current.getBoundingClientRect();
      let left = this.clickX - offsetRect.x;
      let top = this.clickY - offsetRect.y;
      let width = 0;
      let height = 0;
      let config = null;

      if (offset) {
        // support an additional offset against the original click position of the target
        left += offset.left || 0;
        top += offset.top || 0;
      }

      if (position) {
        // escape hatch to define our own position to put the popover
        left = position.left;
        top = position.top;
        width = position.width || 0;
        height = position.height || 0;

        // there's a 20px offset between the svg overlay and the popover container
        left += popoverConfig?.positionOffset?.left ?? 0;
        top += popoverConfig?.positionOffset?.top ?? 0;
      }

      if (!panel) {
        // at this point we have a raw config and will probably want to decorate it
        // with additional details that haven't been submitted via the openPopover call
        config = makeConfig(initialConfig);
      }

      // Clear general topology context when user interacts with specific nodes
      if ($journeys && config) {
        this.clearGeneralTopologyContext();
        // Clear all existing entity focus contexts when selecting a new entity
        this.clearAllEntityFocusContexts();
      }

      this.setState({ position: { left, top, width, height }, config, panel });

      // Add AI Advisor context for node selection
      if (config && $journeys) {
        this.addAIContext('entity_focus', {
          nodeType: config.type,
          nodeValue: config.value,
          cloudProvider: config.cloudProvider,
          nodeData: config.nodeData,
          action: 'select'
        });
      }

      // sidebar opens automatically if show details button is disabled
      if (config?.isShowDetailsButtonDisabled) {
        const { shortcutMenu } = config;
        setSidebarDetails({
          ...shortcutMenu.selectedNode,
          ...config
        });
      }
    };

    clearGeneralTopologyContext = () => {
      const { $journeys } = this.props;

      if (!$journeys) {
        return;
      }

      // Remove all general topology overview contexts when user focuses on specific entities
      const generalContextsToRemove = [
        { type: 'topology_overview', name: 'On Prem Routers' },
        { type: 'topology_overview', name: 'On Prem Topology' },
        { type: 'topology_overview', name: 'AWS Regions' },
        { type: 'topology_overview', name: 'AWS Topology' },
        { type: 'topology_overview', name: 'Azure Regions' },
        { type: 'topology_overview', name: 'Azure Topology' }
      ];

      generalContextsToRemove.forEach((context) => {
        $journeys.removeChatContext({
          ...context,
          context: 'General topology context'
        });
      });
    };

    clearAllEntityFocusContexts = () => {
      const { $journeys } = this.props;

      if (!$journeys) {
        return;
      }

      // Get all current contexts and remove any with type 'entity_focus'
      if ($journeys.activeChatContext && typeof $journeys.activeChatContext.forEach === 'function') {
        const entityFocusContexts = [];
        $journeys.activeChatContext.forEach((context) => {
          if (context.type === 'entity_focus') {
            entityFocusContexts.push(context);
          }
        });

        // Remove all found entity_focus contexts
        entityFocusContexts.forEach((context) => {
          $journeys.removeChatContext({
            type: context.type,
            name: context.name,
            context: 'Previous entity focus context'
          });
        });
      }
    };

    handlePopoverClose = () => {
      this.setState({ position: null, config: null, panel: null });
    };

    get getTitleAndHealth() {
      const { config } = this.state;
      if (config.showTitle || config.showHealth) {
        const { id, name, typeLabel, health } = config.nodeData;
        const { intent, text } = getHealthStateFromHealthData(health);
        return (
          <Box m={1}>
            {config.showTitle && (
              <>
                <Text small as="div">
                  {typeLabel || capitalize((config.subType || config.type)?.replace(/s$/, '') || 'item')}
                </Text>
                <Text fontWeight="bold" as="div" mb={1}>
                  {name || id}
                </Text>
              </>
            )}
            {config.showHealth && (
              <Tag intent={intent} minimal round>
                {text}
              </Tag>
            )}
          </Box>
        );
      }
      return null;
    }

    addAIContext = (contextType, contextData) => {
      const { $journeys } = this.props;

      if (!$journeys) {
        return;
      }

      // Handle cloud provider selection from overview map differently than entity focus
      if (contextData.nodeType === 'cloud' && contextType === 'entity_focus') {
        // For cloud selections from overview map, the provider is in nodeValue
        const cloudProvider = contextData.cloudProvider || contextData.nodeValue;
        if (cloudProvider) {
          this.addCloudProviderContext(cloudProvider);
          return;
        }
      }

      try {
        // Disambiguate gateway types - use nodeData.type if available when config.type is 'gateway'
        const disambiguatedNodeType =
          contextData.nodeType === 'gateway' && contextData.nodeData?.type
            ? contextData.nodeData.type
            : contextData.nodeType;

        let contextName;
        if (contextData.contextName) {
          contextName = contextData.contextName;
        } else if (typeof disambiguatedNodeType !== 'undefined' && typeof contextData.nodeValue !== 'undefined') {
          contextName = `${disambiguatedNodeType} ${contextData.nodeValue}`;
        } else {
          contextName = 'unknown_context';
          console.warn(
            'Context name could not be constructed: missing nodeType or nodeValue in contextData',
            contextData
          );
        }

        // Create a clean, serializable model without circular references
        const cleanNodeData = this.createCleanNodeData(contextData.nodeData);

        // Get custom icon for VPC Endpoints using the existing logic
        let customIcon = null;
        if (disambiguatedNodeType === 'VpcEndpoint' || disambiguatedNodeType === 'VpcEndpoints') {
          if (contextData.nodeData) {
            const vpcEndpointProps = getVpcEndpointProps(contextData.nodeData);
            customIcon = vpcEndpointProps.icon;
          }
        }

        // Create rich context for AI Advisor using the correct format
        const aiContext = {
          type: contextType,
          name: contextName,
          entity: getEntityTypeForIcon(disambiguatedNodeType, contextData.nodeData),
          context: this.buildContextString({ ...contextData, nodeType: disambiguatedNodeType }),
          model: {
            nodeType: disambiguatedNodeType,
            nodeValue: contextData.nodeValue,
            cloudProvider: contextData.cloudProvider,
            action: contextData.action,
            nodeData: cleanNodeData
          },
          suggestions: this.getSuggestionsForNode({ ...contextData, nodeType: disambiguatedNodeType })
        };

        // Add custom icon if we have one
        if (customIcon) {
          aiContext.icon = customIcon;
        }

        $journeys.addChatContext(aiContext);
      } catch (error) {
        console.warn('Failed to add AI context:', error);
      }
    };

    addCloudProviderContext = (cloudProvider) => {
      const { $journeys } = this.props;

      if (!$journeys) {
        return;
      }

      // Remove any existing cloud provider contexts from overview map selection
      // to avoid accumulating multiple cloud provider contexts
      if ($journeys.activeChatContext && typeof $journeys.activeChatContext.forEach === 'function') {
        const existingCloudProviderContexts = [];
        $journeys.activeChatContext.forEach((context) => {
          if (
            context.type === 'topology_loaded' &&
            (context.name === 'Cloud AWS' ||
              context.name === 'Cloud Azure' ||
              context.name === 'Cloud GCP' ||
              context.name === 'Cloud OCI')
          ) {
            existingCloudProviderContexts.push(context);
          }
        });

        // Remove all found cloud provider contexts
        existingCloudProviderContexts.forEach((context) => {
          $journeys.removeChatContext({
            type: context.type,
            name: context.name,
            context: 'Removing previous cloud provider context'
          });
        });
      }

      // Map cloud provider codes to proper names
      const providerNames = {
        aws: 'AWS',
        azure: 'Azure',
        gcp: 'GCP',
        oci: 'OCI'
      };

      const providerName = providerNames[cloudProvider] || cloudProvider.toUpperCase();
      const contextName = `Cloud ${providerName}`;

      const aiContext = {
        type: 'topology_loaded',
        name: contextName,
        entity: 'topology_loaded', // This will trigger the cloud provider logo to be shown
        context: `User selected ${providerName} cloud provider from the overview map. This context provides access to ${providerName} cloud topology, resources, and network connectivity information.`,
        model: {
          cloudProvider,
          nodeType: 'cloud',
          action: 'select'
        },
        suggestions: [
          `Show me the ${providerName} topology`,
          `What resources do I have in ${providerName}?`,
          `Explain ${providerName} connectivity`,
          `Show ${providerName} traffic patterns`
        ]
      };

      $journeys.addChatContext(aiContext);
    };

    createCleanNodeData = (nodeData) => createCleanNodeDataUtil(nodeData);

    buildContextString = (contextData) => {
      const { nodeType, nodeValue, cloudProvider, nodeData, action } = contextData;

      let contextString = `User ${action === 'show_details' ? 'opened details for' : action} ${nodeType} "${nodeValue}" on ${cloudProvider} cloud map.`;

      // Add specific details based on node type
      if (nodeData) {
        switch (nodeType) {
          // AWS entity types
          case 'subnet':
            contextString += ` Subnet details: CIDR ${nodeData.CidrBlock}, VPC ${nodeData.VpcId}, AZ ${nodeData.AvailabilityZone}.`;
            break;
          case 'vpc':
            contextString += ` VPC details: CIDR ${nodeData.CidrBlock}, State ${nodeData.State}, Region ${nodeData.RegionName}.`;
            break;
          case 'gateway': {
            // Fallback case for when gateway type couldn't be disambiguated
            const isTransitGw =
              nodeData.type === 'TransitGateways' ||
              nodeData.GatewayType === 'Transit' ||
              (typeof nodeValue === 'string' && nodeValue.startsWith('tgw-'));
            if (isTransitGw) {
              contextString +=
                ` Transit Gateway details: State ${nodeData.State}, Default Route Table ${nodeData.DefaultRouteTableId || 'N/A'}, ` +
                `Default Propagation ${nodeData.DefaultRouteTablePropagation || 'N/A'}.`;
            } else {
              contextString += ` Gateway details: Type ${nodeData.GatewayType || nodeData.type || 'Unknown'}, State ${nodeData.State}.`;
            }
            break;
          }
          case 'TransitGateways':
            contextString +=
              ` Transit Gateway details: State ${nodeData.State}, Default Route Table ${nodeData.DefaultRouteTableId || 'N/A'}, ` +
              `Default Propagation ${nodeData.DefaultRouteTablePropagation || 'N/A'}.`;
            break;
          case 'TransitGatewayAttachments':
            contextString += ` Transit Gateway Attachment details: State ${nodeData.State}, Resource Type ${nodeData.ResourceType}, Resource ID ${nodeData.ResourceId}.`;
            break;
          case 'InternetGateways':
            contextString += ` Internet Gateway details: State ${nodeData.State}, VPC ${nodeData.VpcId || 'N/A'}.`;
            break;
          case 'CustomerGateways':
            contextString += ` Customer Gateway details: State ${nodeData.State}, BGP ASN ${nodeData.BgpAsn || 'N/A'}, IP Address ${nodeData.IpAddress || 'N/A'}.`;
            break;
          case 'VirtualGateways':
            contextString += ` Virtual Private Gateway details: State ${nodeData.State}, VPC ${nodeData.VpcId || 'N/A'}.`;
            break;
          case 'DirectConnectGateways':
            contextString += ` Direct Connect Gateway details: State ${nodeData.State}, Name ${nodeData.Name || 'N/A'}.`;
            break;
          case 'NatGateways':
            contextString += ` NAT Gateway details: State ${nodeData.State}, Subnet ${nodeData.SubnetId || 'N/A'}, VPC ${nodeData.VpcId || 'N/A'}.`;
            break;
          case 'VpcEndpoint':
          case 'VpcEndpoints':
            contextString += ` VPC Endpoint details: Type ${nodeData.VpcEndpointType || 'N/A'}, Service ${nodeData.ServiceName || 'N/A'}, VPC ${nodeData.VpcId || 'N/A'}.`;
            if (nodeData.FirewallId) {
              contextString += ` Connected to Firewall ${nodeData.FirewallId}.`;
            }
            break;
          case 'region':
            contextString += ` Region details: ${nodeData.Name}, contains ${nodeData.VpcCount || 0} VPCs.`;
            break;
          // Azure entity types
          case VNET:
            contextString += ` VNet details: Address space ${nodeData.addressSpace || nodeData.addressSpaces || 'N/A'}, Location ${nodeData.location}, State ${nodeData.state || nodeData.provisioningState}.`;
            break;
          case SUBNET:
            contextString += ` Subnet details: Address prefix ${nodeData.addressPrefix}, VNet ${nodeData.vnetId || nodeData.virtualNetworkName}, Location ${nodeData.location}.`;
            break;
          case LOCATION:
            contextString += ` Location details: ${nodeData.displayName || nodeData.name}, contains ${nodeData.vnetCount || 0} VNets.`;
            break;
          case VIRTUAL_HUB:
            contextString += ` Virtual Hub details: Address prefix ${nodeData.addressPrefix}, Location ${nodeData.location}, State ${nodeData.provisioningState}.`;
            break;
          case VIRTUAL_WAN:
            contextString += ` Virtual WAN details: Type ${nodeData.type}, Location ${nodeData.location}, State ${nodeData.provisioningState}.`;
            break;
          case VPN_SITE:
            contextString += ` VPN Site details: Location ${nodeData.location}, BGP ASN ${nodeData.bgpAsn || 'N/A'}, IP Address ${nodeData.ipAddress || 'N/A'}.`;
            break;
          case EXPRESS_ROUTE_CIRCUIT:
            contextString += ` ExpressRoute Circuit details: Bandwidth ${nodeData.bandwidth || 'N/A'}, Location ${nodeData.location}, State ${nodeData.circuitProvisioningState}.`;
            break;
          case LOAD_BALANCER:
            contextString += ` Load Balancer details: Type ${nodeData.type}, Location ${nodeData.location}, State ${nodeData.provisioningState}.`;
            break;
          case APPLICATION_GATEWAY:
            contextString += ` Application Gateway details: Tier ${nodeData.tier || nodeData.sku?.tier}, Location ${nodeData.location}, State ${nodeData.provisioningState}.`;
            break;
          case FIREWALL:
            contextString += ` Firewall details: Location ${nodeData.location}, State ${nodeData.provisioningState}, Threat intel mode ${nodeData.threatIntelMode || 'N/A'}.`;
            break;
          case LOCAL_NETWORK_GATEWAY:
            contextString += ` Local Network Gateway details: Gateway IP ${nodeData.gatewayIpAddress}, Location ${nodeData.location}, State ${nodeData.provisioningState}.`;
            break;
          case VNET_GATEWAY:
            contextString += ` VNet Gateway details: Type ${nodeData.gatewayType}, VPN type ${nodeData.vpnType}, Location ${nodeData.location}, State ${nodeData.provisioningState}.`;
            break;

          // OCI entity types
          case VIRTUAL_CLOUD_NETWORK:
            contextString += ` VCN details: CIDR block ${nodeData.cidrBlock || nodeData.cidrBlocks}, Compartment ${nodeData.compartmentName || nodeData.compartmentId}, State ${nodeData.lifecycleState}.`;
            break;
          case OCI_SUBNET:
            contextString += ` Subnet details: CIDR block ${nodeData.cidrBlock}, VCN ${nodeData.vcnId}, AD ${nodeData.availabilityDomain || 'N/A'}, State ${nodeData.lifecycleState}.`;
            break;
          case OCI_INTERNET_GATEWAY:
            contextString += ` Internet Gateway details: VCN ${nodeData.vcnId}, Enabled ${nodeData.isEnabled ? 'Yes' : 'No'}, State ${nodeData.lifecycleState}.`;
            break;
          case OCI_NAT_GATEWAY:
            contextString += ` NAT Gateway details: VCN ${nodeData.vcnId}, Public IP ${nodeData.natIp || 'N/A'}, State ${nodeData.lifecycleState}.`;
            break;
          case SERVICE_GATEWAY:
            contextString += ` Service Gateway details: VCN ${nodeData.vcnId}, Services ${nodeData.services?.length || 0}, State ${nodeData.lifecycleState}.`;
            break;
          case DYNAMIC_ROUTING_GATEWAY:
            contextString += ` DRG details: Compartment ${nodeData.compartmentName || nodeData.compartmentId}, State ${nodeData.lifecycleState}.`;
            break;
          case LOCAL_PEERING_GATEWAY:
            contextString += ` Local Peering Gateway details: VCN ${nodeData.vcnId}, Peer VCN ${nodeData.peerVcnId || 'N/A'}, State ${nodeData.lifecycleState}.`;
            break;
          case INSTANCE:
            contextString += ` Instance details: Shape ${nodeData.shape}, AD ${nodeData.availabilityDomain}, State ${nodeData.lifecycleState}.`;
            break;
          case OCI_ROUTER:
            contextString += ` Router details: Compartment ${nodeData.compartmentName || nodeData.compartmentId}, State ${nodeData.lifecycleState || 'Active'}.`;
            break;

          // GCP entity types
          case NETWORK:
            contextString += ` Network details: Project ${nodeData.project}, Region ${nodeData.region || 'Global'}, Type ${nodeData.gatewayIPv4 ? 'Legacy' : 'VPC-native'}.`;
            break;
          case GCP_SUBNET:
            contextString += ` Subnet details: IP range ${nodeData.ipCidrRange}, Network ${nodeData.network}, Region ${nodeData.region}, Gateway ${nodeData.gatewayAddress}.`;
            break;
          case EXTERNAL_VPN_GATEWAY:
            contextString += ` External VPN Gateway details: Interface count ${nodeData.interfaces?.length || 0}, Redundancy type ${nodeData.redundancyType || 'N/A'}.`;
            break;
          case GCP_ROUTER:
            contextString += ` Router details: Network ${nodeData.network}, Region ${nodeData.region}, BGP ASN ${nodeData.bgp?.asn || 'N/A'}.`;
            break;
          case INTERCONNECT:
            contextString += ` Interconnect details: Type ${nodeData.interconnectType}, Link type ${nodeData.linkType}, State ${nodeData.state}, Customer ${nodeData.customerName || 'N/A'}.`;
            break;
          case INTERCONNECT_ATTACHMENT:
            contextString += ` Interconnect Attachment details: Type ${nodeData.type}, State ${nodeData.state}, VLAN ${nodeData.vlanTag8021q || 'N/A'}.`;
            break;
          case VPN_GATEWAY:
            contextString += ` VPN Gateway details: Network ${nodeData.network}, Region ${nodeData.region}, State ${nodeData.status || 'N/A'}.`;
            break;
          case VPN_TUNNEL:
            contextString += ` VPN Tunnel details: Status ${nodeData.status}, Target VPN Gateway ${nodeData.targetVpnGateway || 'N/A'}, Peer IP ${nodeData.peerIp || 'N/A'}.`;
            break;
          case CLOUD_ROUTER:
            contextString += ` Cloud Router details: Network ${nodeData.network}, Region ${nodeData.region}, BGP ASN ${nodeData.bgp?.asn || 'N/A'}.`;
            break;
          case TARGET_VPN_GATEWAY:
            contextString += ` Target VPN Gateway details: Network ${nodeData.network}, Region ${nodeData.region}, Status ${nodeData.status || 'N/A'}.`;
            break;
          case GCP_NAT_GATEWAY:
            contextString += ` NAT Gateway details: Router ${nodeData.router}, Region ${nodeData.region}, Type ${nodeData.natIpAllocateOption || 'N/A'}.`;
            break;
          case GCP_REGION:
            contextString += ` Region details: ${nodeData.description || nodeData.name}, Zone count ${nodeData.zones?.length || 0}, Status ${nodeData.status || 'UP'}.`;
            break;
          case GCP_FIREWALL:
            contextString += ` Firewall details: Network ${nodeData.network}, Direction ${nodeData.direction}, Priority ${nodeData.priority || 'N/A'}, Action ${nodeData.allowed ? 'Allow' : 'Deny'}.`;
            break;
          default:
            contextString += ` ${nodeType} details available in model object.`;
        }
      }

      return contextString;
    };

    getSuggestionsForNode = (contextData) => {
      const { nodeType, action, nodeData } = contextData;

      const suggestions = [];

      if (action === 'select' || action === 'show_details') {
        switch (nodeType) {
          case 'subnet':
            suggestions.push(
              'Show me the traffic patterns for this subnet',
              'What security groups are applied to this subnet?',
              'Show me the route table for this subnet',
              'Explain the purpose of this subnet'
            );
            break;
          case 'vpc':
            suggestions.push(
              'Show me all subnets in this VPC',
              'What are the network ACLs for this VPC?',
              'Show me the VPC flow logs',
              'Explain the architecture of this VPC'
            );
            break;
          case 'gateway': {
            // Fallback case for when gateway type couldn't be disambiguated
            const isTransitGateway =
              nodeData?.type === TRANSIT_GATEWAY ||
              nodeData?.GatewayType === 'Transit' ||
              (typeof nodeData?.value === 'string' && nodeData.value.startsWith('tgw-'));
            if (isTransitGateway) {
              suggestions.push(
                'Show me all attachments for this Transit Gateway',
                'What VPCs are connected to this Transit Gateway?',
                'Show me the route tables for this Transit Gateway',
                'Explain the Transit Gateway routing configuration'
              );
            } else {
              suggestions.push(
                'Show me traffic through this gateway',
                'What resources are connected to this gateway?',
                'Check the health status of this gateway',
                'Explain how this gateway is configured'
              );
            }
            break;
          }
          case TRANSIT_GATEWAY:
            suggestions.push(
              'Show me all attachments for this Transit Gateway',
              'What VPCs are connected to this Transit Gateway?',
              'Show me the route tables for this Transit Gateway',
              'Explain the Transit Gateway routing configuration'
            );
            break;
          case TRANSIT_GATEWAY_ATTACHMENT:
            suggestions.push(
              'Show me traffic through this Transit Gateway attachment',
              'What is the purpose of this attachment?',
              'Show me the route propagation for this attachment',
              'Explain how this attachment is configured'
            );
            break;
          case INTERNET_GATEWAY:
            suggestions.push(
              'Show me internet traffic through this gateway',
              'What VPC is this Internet Gateway attached to?',
              'Check the routing tables for this Internet Gateway',
              'Explain the Internet Gateway configuration'
            );
            break;
          case CUSTOMER_GATEWAY:
            suggestions.push(
              'Show me VPN connections for this Customer Gateway',
              'What is the BGP configuration for this gateway?',
              'Check the tunnel status for this Customer Gateway',
              'Explain the VPN setup for this gateway'
            );
            break;
          case 'VirtualGateways':
            suggestions.push(
              'Show me VPN connections for this Virtual Private Gateway',
              'What VPC is this Virtual Private Gateway attached to?',
              'Check the route propagation for this gateway',
              'Explain the VPN configuration'
            );
            break;
          case DIRECT_CONNECTION_GATEWAY:
            suggestions.push(
              'Show me Direct Connect connections for this gateway',
              'What virtual interfaces are attached to this gateway?',
              'Check the routing configuration for this Direct Connect Gateway',
              'Explain the Direct Connect setup'
            );
            break;
          case NAT_GATEWAY:
            suggestions.push(
              'Show me outbound traffic through this NAT Gateway',
              'What subnet is this NAT Gateway in?',
              'Check the route tables pointing to this NAT Gateway',
              'Explain the NAT Gateway configuration'
            );
            break;
          case 'VpcEndpoint':
          case 'VpcEndpoints':
            suggestions.push(
              'Show me traffic through this VPC Endpoint',
              'What service does this VPC Endpoint connect to?',
              'Check the route tables for this VPC Endpoint',
              'Explain how this VPC Endpoint is configured'
            );
            break;
          case 'region':
            suggestions.push(
              'Show me all resources in this region',
              'What is the network topology for this region?',
              'Show me costs for this region',
              'Compare this region to other regions'
            );
            break;

          // Azure entity types
          case VNET:
            suggestions.push(
              'Show me all subnets in this VNet',
              'What are the network security groups for this VNet?',
              'Show me VNet peering connections',
              'Explain the architecture of this VNet'
            );
            break;
          case SUBNET:
            suggestions.push(
              'Show me the traffic patterns for this subnet',
              'What network security groups are applied to this subnet?',
              'Show me the route table for this subnet',
              'Explain the purpose of this subnet'
            );
            break;
          case LOCATION:
            suggestions.push(
              'Show me all resources in this location',
              'What is the network topology for this location?',
              'Show me costs for this location',
              'Compare this location to other locations'
            );
            break;
          case VIRTUAL_HUB:
            suggestions.push(
              'Show me all connections to this Virtual Hub',
              'What VNets are connected to this Virtual Hub?',
              'Show me the route tables for this Virtual Hub',
              'Explain the Virtual Hub routing configuration'
            );
            break;
          case VIRTUAL_WAN:
            suggestions.push(
              'Show me all Virtual Hubs in this Virtual WAN',
              'What sites are connected to this Virtual WAN?',
              'Explain the Virtual WAN architecture',
              'Show me the connectivity topology'
            );
            break;
          case VPN_SITE:
            suggestions.push(
              'Show me VPN connections for this site',
              'What is the BGP configuration for this site?',
              'Check the tunnel status for this VPN site',
              'Explain the VPN setup for this site'
            );
            break;
          case EXPRESS_ROUTE_CIRCUIT:
            suggestions.push(
              'Show me ExpressRoute connections for this circuit',
              'What is the bandwidth utilization?',
              'Check the BGP peering status',
              'Explain the ExpressRoute configuration'
            );
            break;
          case LOAD_BALANCER:
            suggestions.push(
              'Show me traffic through this load balancer',
              'What backend pools are configured?',
              'Check the health probe status',
              'Explain the load balancing rules'
            );
            break;
          case APPLICATION_GATEWAY:
            suggestions.push(
              'Show me traffic through this application gateway',
              'What backend pools are configured?',
              'Check the health probe status',
              'Explain the routing rules and SSL configuration'
            );
            break;
          case FIREWALL:
            suggestions.push(
              'Show me traffic through this firewall',
              'What firewall rules are configured?',
              'Check the threat intelligence status',
              'Explain the firewall policy configuration'
            );
            break;
          case LOCAL_NETWORK_GATEWAY:
            suggestions.push(
              'Show me connections for this local network gateway',
              'What on-premises networks are represented?',
              'Check the BGP configuration',
              'Explain the VPN connection setup'
            );
            break;
          case VNET_GATEWAY:
            suggestions.push(
              'Show me connections for this VNet gateway',
              'What type of VPN or ExpressRoute connections?',
              'Check the gateway performance metrics',
              'Explain the gateway configuration'
            );
            break;

          // OCI entity types
          case VIRTUAL_CLOUD_NETWORK:
            suggestions.push(
              'Show me all subnets in this VCN',
              'What are the security lists for this VCN?',
              'Show me VCN peering connections',
              'Explain the architecture of this VCN'
            );
            break;
          case OCI_SUBNET:
            suggestions.push(
              'Show me the traffic patterns for this subnet',
              'What security lists are applied to this subnet?',
              'Show me the route table for this subnet',
              'Explain the purpose of this subnet'
            );
            break;
          case OCI_INTERNET_GATEWAY:
            suggestions.push(
              'Show me internet traffic through this gateway',
              'What route tables point to this gateway?',
              'Check the security rules for internet access',
              'Explain how internet connectivity is configured'
            );
            break;
          case OCI_NAT_GATEWAY:
            suggestions.push(
              'Show me outbound traffic through this NAT Gateway',
              'What subnets use this NAT Gateway?',
              'Check the route tables pointing to this NAT Gateway',
              'Explain the NAT Gateway configuration'
            );
            break;
          case SERVICE_GATEWAY:
            suggestions.push(
              'Show me Oracle service connections through this gateway',
              'What Oracle services are accessible?',
              'Check the route tables for service gateway routes',
              'Explain the service gateway configuration'
            );
            break;
          case DYNAMIC_ROUTING_GATEWAY:
            suggestions.push(
              'Show me all attachments for this DRG',
              'What VCNs are connected to this DRG?',
              'Show me the route tables for this DRG',
              'Explain the DRG routing configuration'
            );
            break;
          case LOCAL_PEERING_GATEWAY:
            suggestions.push(
              'Show me peering connections for this gateway',
              'What VCN is this gateway peering with?',
              'Check the route propagation for this peering',
              'Explain the local peering configuration'
            );
            break;
          case INSTANCE:
            suggestions.push(
              'Show me network traffic for this instance',
              'What VNICs are attached to this instance?',
              'Check the security group rules for this instance',
              'Explain the instance network configuration'
            );
            break;
          case OCI_ROUTER:
            suggestions.push(
              'Show me routing configuration for this router',
              'What networks are connected to this router?',
              'Check the route tables and policies',
              'Explain the router setup and connectivity'
            );
            break;

          // GCP entity types
          case NETWORK:
            suggestions.push(
              'Show me all subnets in this network',
              'What are the firewall rules for this network?',
              'Show me network peering connections',
              'Explain the architecture of this network'
            );
            break;
          case GCP_SUBNET:
            suggestions.push(
              'Show me traffic patterns for this subnet',
              'What instances are in this subnet?',
              'Show me the route table for this subnet',
              'Explain the purpose of this subnet'
            );
            break;
          case EXTERNAL_VPN_GATEWAY:
            suggestions.push(
              'Show me VPN tunnels for this external gateway',
              'What is the redundancy configuration?',
              'Check the tunnel status and health',
              'Explain the external VPN setup'
            );
            break;
          case GCP_ROUTER:
            suggestions.push(
              'Show me routing configuration for this router',
              'What networks are connected to this router?',
              'Check the BGP sessions for this router',
              'Explain the router setup and connectivity'
            );
            break;
          case INTERCONNECT:
            suggestions.push(
              'Show me interconnect attachments',
              'What is the bandwidth utilization?',
              'Check the interconnect status and health',
              'Explain the interconnect configuration'
            );
            break;
          case INTERCONNECT_ATTACHMENT:
            suggestions.push(
              'Show me traffic through this attachment',
              'What VLANs are configured?',
              'Check the attachment status',
              'Explain the attachment configuration'
            );
            break;
          case VPN_GATEWAY:
            suggestions.push(
              'Show me VPN tunnels for this gateway',
              'What is the VPN configuration?',
              'Check the tunnel status and health',
              'Explain the VPN setup'
            );
            break;
          case VPN_TUNNEL:
            suggestions.push(
              'Show me traffic through this VPN tunnel',
              'What is the tunnel configuration?',
              'Check the tunnel health and status',
              'Explain the VPN tunnel setup'
            );
            break;
          case CLOUD_ROUTER:
            suggestions.push(
              'Show me BGP sessions for this Cloud Router',
              'What are the learned routes?',
              'Check the router configuration',
              'Explain the Cloud Router setup'
            );
            break;
          case TARGET_VPN_GATEWAY:
            suggestions.push(
              'Show me VPN tunnels for this target gateway',
              'What forwarding rules are configured?',
              'Check the gateway status',
              'Explain the target VPN gateway setup'
            );
            break;
          case GCP_NAT_GATEWAY:
            suggestions.push(
              'Show me outbound traffic through this NAT',
              'What subnets use this NAT gateway?',
              'Check the NAT IP allocation',
              'Explain the NAT gateway configuration'
            );
            break;
          case GCP_REGION:
            suggestions.push(
              'Show me all resources in this region',
              'What is the network topology for this region?',
              'Show me costs for this region',
              'Compare this region to other regions'
            );
            break;
          case GCP_FIREWALL:
            suggestions.push(
              'Show me traffic affected by this firewall rule',
              'What are the source and target configurations?',
              'Check if this rule is being used',
              'Explain the firewall rule purpose'
            );
            break;

          default:
            // Generic suggestions for unknown entity types
            suggestions.push(
              `Explain this ${nodeType} in detail`,
              `What is the purpose of this ${nodeType}?`,
              'Show me related resources',
              'Are there any configuration issues?'
            );
        }
      } else if (action === 'show_connections') {
        suggestions.push(
          'Explain these network connections in detail',
          'Show me traffic flows between these resources',
          'Are there any security issues with these connections?',
          'How can I optimize these network paths?'
        );
      }

      return suggestions;
    };

    get topologyLink() {
      const { history, $hybridMap } = this.props;
      const { config } = this.state;
      const validNodeTypes = ['cloud', 'device', 'region', 'site', 'vpc'];

      if (!config.isEmbedded && validNodeTypes.includes(config.type)) {
        let mapType = config.type;
        let id = config.value;

        if (config.type === 'site') {
          id = config.id;
        }

        if (config.type === 'region') {
          mapType = `cloud/${config.cloudProvider}/region`;
        }

        if (config.type === 'vpc') {
          mapType = `cloud/${config.cloudProvider}/region/${config.cloudRegion}/vpc`;
        }

        return (
          <MenuItem
            text="View Topology"
            onClick={() => history.push(`${$hybridMap.baseRoute}/${mapType}/${id}`)}
            icon={IoIosGitNetwork}
          />
        );
      }

      return null;
    }

    get embeddedMapLink() {
      const { history, $hybridMap, device } = this.props;
      const { config } = this.state;
      const validNodeTypes = ['cloud', 'device', 'site', 'interface'];

      if (config.isEmbedded && validNodeTypes.includes(config.type)) {
        let mapType = config.type;
        let id = config.value;

        if (config.type === 'site') {
          id = config.id;
        }

        if (config.type === 'interface') {
          mapType = 'device';
          id = device ? device.id : config.value.device_id;
        }

        return (
          <MenuItem
            text="View Full Map"
            onClick={() => history.push(`${$hybridMap.baseRoute}/${mapType}/${id}`)}
            icon={IoIosGitNetwork}
          />
        );
      }

      return null;
    }

    get popoverContent() {
      const { setSidebarDetails } = this.props;
      const { config, panel } = this.state;

      if (panel) {
        return panel;
      }

      const { shortcutMenu, showConnectionsMenuItemProps } = config;

      // if we have an actual selectedNode properties, display the shortcut menu
      return (
        <Menu>
          {this.getTitleAndHealth}
          {this.topologyLink}
          {this.embeddedMapLink}
          {!config.isEmbedded && !config.isShowDetailsButtonDisabled && (
            <MenuItem
              text="Show Details"
              icon={FaEllipsisH}
              onClick={() => {
                const { $journeys } = this.props;

                // Add AI context BEFORE setting sidebar details to ensure it persists
                if ($journeys) {
                  // Clear general topology context first
                  this.clearGeneralTopologyContext();
                  // Clear all existing entity focus contexts
                  this.clearAllEntityFocusContexts();

                  // Clear any existing show details context

                  // no need to add contextName here as it's constructed in addAIContext
                  this.addAIContext('entity_focus', {
                    nodeType: config.type,
                    nodeValue: config.value,
                    cloudProvider: config.cloudProvider,
                    nodeData: config.nodeData,
                    action: 'show_details'
                  });
                }

                setSidebarDetails({
                  ...shortcutMenu.selectedNode,
                  ...config
                });

                this.handlePopoverClose();
              }}
            />
          )}
          {!shortcutMenu.hideShowConnectionsMenuItem && (
            <MenuItem
              text={`${shortcutMenu.isShowingConnections ? 'Hide' : 'Show'} Connections`}
              icon="flow-review"
              onClick={() => {
                const { $journeys } = this.props;
                const node = shortcutMenu.isShowingConnections ? null : shortcutMenu.selectedNode;

                shortcutMenu.showConnectionsCallback(node);

                // Add AI context for connection toggles with focused suggestions
                if ($journeys) {
                  if (node && !shortcutMenu.isShowingConnections) {
                    // Showing connections
                    this.addAIContext('entity_focus', {
                      nodeType: config.type,
                      nodeValue: config.value,
                      cloudProvider: config.cloudProvider,
                      nodeData: node,
                      action: 'show_connections'
                    });
                  } else if (shortcutMenu.isShowingConnections) {
                    // Hiding connections - remove the connections context
                    const contextToRemove = {
                      type: 'entity_focus',
                      name: `${config.type} ${config.value} connections`,
                      context: 'Network connections context'
                    };
                    $journeys.removeChatContext(contextToRemove);
                  }
                }

                this.handlePopoverClose();
              }}
              {...showConnectionsMenuItemProps}
            />
          )}

          {shortcutMenu.customItems &&
            shortcutMenu.customItems.map((item) => {
              if (React.isValidElement(item)) {
                return item;
              }

              const { text, icon, action } = item;
              return <MenuItem key={text} text={text} icon={icon} onClick={action} />;
            })}
        </Menu>
      );
    }

    get popover() {
      const { position, config } = this.state;
      const { dark, ...popoverProps } = popoverConfig;

      if (position) {
        return (
          <Popover
            isOpen={position !== null}
            position={(config && config.placement) || 'left'}
            content={this.popoverContent}
            popoverClassName={classNames('no-transform', { [Classes.DARK]: dark })}
            minimal={false}
            autoFocus={false}
            onClose={this.handlePopoverClose}
            targetProps={{
              style: {
                position: 'absolute',
                left: position.left,
                top: position.top,
                width: position.width,
                height: position.height
              }
            }}
            {...popoverProps}
          >
            <div />
          </Popover>
        );
      }

      return null;
    }

    render() {
      const { $hybridMap, $journeys, loading, ...restProps } = this.props;
      const { config, position } = this.state;

      return (
        <Suspense loading={loading}>
          <div ref={this.boxRef} style={{ height: '100%', position: 'relative' }}>
            {this.popover}
            <WrappedComponent
              {...restProps}
              setPopoverPosition={this.setPopoverPosition}
              openPopover={this.handlePopoverOpen}
              onPopoverClose={this.handlePopoverClose}
              isPopoverOpen={!!(config && position)}
            />
          </div>
        </Suspense>
      );
    }
  }

  return WrapperComponent;
}
